import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useRole,
  useUpdateRole,
  useSetRolePermissions,
  useAssignRole,
  useRemoveRoleAssignment,
  usePeople,
} from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import {
  roleDisplayName,
  permissionLabel,
  permissionDomainLabel,
  PERMISSION_LABELS,
} from '../../../shared/labels';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import { PersonSelect } from '../../../components/ui/InlineCreate';
import {
  SkeletonDetail,
  inputCss,
  linkBtn,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './role-detail.support';
import { RoleEditModal } from './role-detail.modals';
export function RoleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useRole(id ?? '');
  const updateRole = useUpdateRole();
  const setPermissions = useSetRolePermissions();
  const assignRole = useAssignRole();
  const removeAssignment = useRemoveRoleAssignment();
  const { data: peopleData } = usePeople({ limit: 100, status: 'ACTIVE' });
  const people = asList(peopleData).map((raw) => {
    const p = asRecord(raw);
    return {
      id: str(p.id),
      name: str(p.socialName) || str(p.civilName) || str(p.id),
    };
  });
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [addPermOpen, setAddPermOpen] = useState(false);
  const [newPerm, setNewPerm] = useState('');
  const [assignPersonId, setAssignPersonId] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <button
          type="button"
          onClick={() => {
            void navigate('/permissoes');
          }}
          style={linkBtn}
        >
          ← Voltar
        </button>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)' }}>Não foi possível carregar o papel.</p>
        </Section>
      </div>
    );
  }
  const role = asRecord(data);
  const name = str(role.name);
  const displayName = roleDisplayName(name);
  const permList = asList(role.permissions);
  const assignList = asList(role.assignments);
  const isSystem = !!role.isSystem;
  const assignedKeys = new Set(permList.map((p) => str(asRecord(p).permission)));
  const catalogOptions = Object.entries(PERMISSION_LABELS).filter(
    ([key]) => !assignedKeys.has(key),
  );
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/permissoes');
        }}
        style={linkBtn}
      >
        ← Voltar para permissões de acesso
      </button>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{displayName}</h2>
            {isSystem && <Badge label="Sistema" variant="info" />}
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.25rem 0 0' }}>
            {str(role.description, 'Sem descrição')}
            {' — '}
            {permList.length} permissões, {assignList.length} pessoas
          </p>
        </div>
        {!isSystem && (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
            }}
            style={secondaryBtn}
          >
            Editar
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {editing && (
          <RoleEditModal
            name={name}
            description={str(role.description)}
            editName={editName}
            setEditName={setEditName}
            editDesc={editDesc}
            setEditDesc={setEditDesc}
            onCancel={() => {
              setEditing(false);
            }}
            onSave={() => {
              const d: Record<string, string> = {};
              if (editName && editName !== name) d.name = editName;
              if (editDesc !== str(role.description)) d.description = editDesc;
              if (Object.keys(d).length > 0) updateRole.mutate({ id: id, data: d });
              setEditing(false);
            }}
          />
        )}
        {/* Permissions */}
        <Section
          title="Permissões"
          action={
            <button
              onClick={() => {
                setAddPermOpen(true);
              }}
              style={smallBtn}
            >
              + Adicionar
            </button>
          }
        >
          {addPermOpen && (
            <div
              style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}
            >
              <select
                value={newPerm}
                onChange={(e) => {
                  setNewPerm(e.target.value);
                }}
                style={{ ...inputCss, flex: 1, minWidth: 220 }}
              >
                <option value="">Selecione uma permissão...</option>
                {catalogOptions.map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!newPerm.trim()) return;
                  setPermissions.mutate({
                    id: id,
                    permissions: [
                      ...permList.map((p) => str(asRecord(p).permission)),
                      newPerm.trim(),
                    ],
                  });
                  setNewPerm('');
                  setAddPermOpen(false);
                }}
                style={primaryBtn}
              >
                Adicionar
              </button>
              <button
                onClick={() => {
                  setAddPermOpen(false);
                }}
                style={secondaryBtn}
              >
                Cancelar
              </button>
            </div>
          )}
          {permList.length === 0 ? (
            <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>
              Nenhuma permissão configurada.
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem 1.5rem' }}>
              {permList.map((raw) => {
                const perm = str(asRecord(raw).permission);
                return (
                  <div
                    key={perm}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      fontSize: '0.8rem',
                    }}
                  >
                    <span
                      style={{
                        background: 'var(--info-soft)',
                        color: 'var(--info)',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        padding: '0.1rem 0.35rem',
                        borderRadius: 6,
                      }}
                    >
                      {permissionDomainLabel(perm)}
                    </span>
                    <span style={{ color: 'var(--ink)' }} title={perm}>
                      {permissionLabel(perm)}
                    </span>
                    <button
                      onClick={() => {
                        setPermissions.mutate({
                          id: id,
                          permissions: permList
                            .map((p) => str(asRecord(p).permission))
                            .filter((p) => p !== perm),
                        });
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--danger)',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        padding: 0,
                        marginLeft: 'auto',
                      }}
                      title="Remover"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
        {/* Assignments */}
        <Section
          title="Pessoas atribuídas"
          action={
            <button
              onClick={() => {
                setShowAssign(true);
              }}
              style={smallBtn}
            >
              + Atribuir
            </button>
          }
        >
          {showAssign && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <PersonSelect
                label="Pessoa da casa"
                value={assignPersonId}
                onChange={setAssignPersonId}
                people={people}
                emptyLabel="Selecione uma pessoa..."
              />
              <button
                onClick={() => {
                  if (!assignPersonId.trim()) return;
                  assignRole.mutate({ roleId: id, personId: assignPersonId.trim() });
                  setAssignPersonId('');
                  setShowAssign(false);
                }}
                style={primaryBtn}
              >
                Atribuir
              </button>
              <button
                onClick={() => {
                  setShowAssign(false);
                }}
                style={secondaryBtn}
              >
                Cancelar
              </button>
            </div>
          )}
          {assignList.length === 0 ? (
            <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>
              Nenhuma pessoa atribuída a este papel.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {assignList.map((raw) => {
                const a = asRecord(raw);
                const person = asRecord(a.person);
                const displayName = str(person.socialName ?? person.civilName);
                return (
                  <div
                    key={str(a.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.5rem',
                      borderRadius: 8,
                      background: 'var(--paper)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{displayName || 'Pessoa sem nome'}</span>
                    {str(person.email) && (
                      <span style={{ color: 'var(--faint)', fontSize: '0.75rem' }}>
                        {str(person.email)}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        if (confirm(`Remover ${displayName} deste papel?`)) {
                          removeAssignment.mutate({ roleId: id, assignmentId: str(a.id) });
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--danger)',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        padding: 0,
                        marginLeft: 'auto',
                      }}
                    >
                      ✕ Remover
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
